/**
 * BidCalculationEngine — Pure, stateless functions that compute derived financial totals.
 *
 * This mirrors the backend @property formulas in api/bid/models.py.
 * These functions MUST NOT perform any side effects, API calls, or state mutations.
 *
 * The goal is to compute all derived values locally so the frontend
 * never depends on the backend's denormalized columns (client_quoted, client_budget, etc.).
 *
 * Formula chain (line item level):
 *   vendor_rate → client_rate → client_rate_budget_buffered → client_rate_quoted → total → client_budget / client_quoted
 *
 * Formula chain (group level):
 *   sum(line_item.client_budget_buffered) + sum(children.client_budget_buffered) = group.client_budget_buffered
 *   sum(deep_line_items.client_quoted) + sum(deep_addons.client_quoted) = group.client_quoted
 *
 * Formula chain (addon level):
 *   addon.type === "amount" → addon.amount
 *   addon.type === "percentage" → addon.amount * parent.client_quoted_pre_addon (with optional minimum)
 */

// ── Types ──────────────────────────────────────────────────────────────────

interface LineItemInput {
    vendor_rate: number;
    markup: number;
    markup_amount: number;
    budget_buffer: number;
    budget_buffer_amount: number;
    quantity: number;
    days: number;
    adjustment: number;
    ot_1_5: number;
    ot_2_0: number;
    vendor_client_exchange_rate_buffered: number;
    is_internal: boolean;
    internal_rate?: number | null;
    internal_markup?: number | null;
}

export interface LineItemDerived {
    client_rate: number;
    client_rate_budget_buffered: number;
    client_rate_quoted: number;
    client_budget: number;
    client_budget_buffered: number;
    client_quoted: number;
    client_gross_profit: number;
    internal_budget: number;
}

interface AddonInput {
    id: number;
    amount: number;
    minimum_amount?: number | null;
    type: "amount" | "percentage";
    line_group_id?: number | null;
}

export interface AddonDerived {
    client_quoted: number;
}

export interface GroupDerived {
    client_budget: number;
    client_budget_buffered: number;
    client_quoted: number;
    client_quoted_pre_addon: number;
    client_gross_profit: number;
}

export interface BidDerived {
    client_quoted_pre_addon: number;
    total_quoted: number;
    addons_quoted: number;
    deep_addons_quoted: number;
    external_budget: number;
    internal_budget: number;
    external_quoted: number;
    internal_quoted: number;
}

// ── Utility ────────────────────────────────────────────────────────────────

function roundMoney(value: number): number {
    return Math.round(value * 100) / 100;
}

// ── Line Item Calculations ─────────────────────────────────────────────────

/**
 * Computes all derived financial values for a single line item.
 * Mirrors: LineItemBaseModel @property chain in models.py lines 309-354
 */
export function computeLineItemDerived(item: LineItemInput): LineItemDerived {
    const exchangeRate = item.vendor_client_exchange_rate_buffered || 1;

    // client_rate = vendor_rate × exchange_rate (models.py:311)
    const client_rate = roundMoney(item.vendor_rate * exchangeRate);

    // client_rate_budget_buffered = client_rate × (1 + budget_buffer) + budget_buffer_amount (models.py:314)
    const client_rate_budget_buffered =
        client_rate * (1 + item.budget_buffer) + item.budget_buffer_amount;

    // client_rate_quoted = client_rate_budget_buffered × (1 + markup) + markup_amount (models.py:321)
    const client_rate_quoted =
        client_rate_budget_buffered * (1 + item.markup) + item.markup_amount;

    // total(rate) = rate × days × quantity + OT (models.py:327-330)
    const computeTotal = (rate: number): number => {
        const ot15 = item.ot_1_5 * (rate / 10) * 1.5;
        const ot20 = item.ot_2_0 * (rate / 10) * 2;
        return roundMoney(rate * item.days * item.quantity + ot15 + ot20);
    };

    // client_budget = total(client_rate) (models.py:337-338)
    const client_budget = computeTotal(client_rate);

    // client_budget_buffered = total(client_rate_budget_buffered) (models.py:345-346)
    const client_budget_buffered = computeTotal(client_rate_budget_buffered);

    // client_quoted = total(client_rate_quoted) + adjustment (models.py:349-350)
    const client_quoted = computeTotal(client_rate_quoted) + item.adjustment;

    // client_gross_profit = client_quoted - client_budget (models.py:353-354)
    const client_gross_profit = client_quoted - client_budget;

    // internal_budget = total(internal_rate) if internal_rate exists (models.py:333-334)
    const internal_budget = item.internal_rate
        ? computeTotal(item.internal_rate)
        : 0;

    return {
        client_rate,
        client_rate_budget_buffered,
        client_rate_quoted,
        client_budget,
        client_budget_buffered,
        client_quoted,
        client_gross_profit,
        internal_budget,
    };
}

// ── Addon Calculations ─────────────────────────────────────────────────────

/**
 * Computes the derived quoted value for a single addon.
 * Mirrors: Addon.client_quoted_property in models.py lines 447-457
 *
 * @param addon - The addon configuration
 * @param parentQuotedPreAddon - The parent's client_quoted_pre_addon
 *        (group's pre-addon total if line_group_id is set, bid's pre-addon total otherwise)
 */
export function computeAddonDerived(
    addon: AddonInput,
    parentQuotedPreAddon: number,
): AddonDerived {
    if (addon.type === "amount") {
        return { client_quoted: addon.amount };
    }

    // type === "percentage"
    const amount = addon.amount * parentQuotedPreAddon;
    const client_quoted =
        addon.minimum_amount != null
            ? Math.max(addon.minimum_amount, amount)
            : amount;

    return { client_quoted };
}

// ── Group Calculations ─────────────────────────────────────────────────────

interface GroupInputNode {
    id: number;
    children: GroupInputNode[];
    lineItems: LineItemInput[];
    addons: AddonInput[];
}

interface GroupComputedNode {
    id: number;
    derived: GroupDerived;
    lineItemsDerived: LineItemDerived[];
    addonsDerived: AddonDerived[];
    children: GroupComputedNode[];
}

/**
 * Recursively computes derived totals for a group and all its descendants.
 * Mirrors: LineGroupBaseModel @property chain in models.py lines 54-168
 */
export function computeGroupDerived(group: GroupInputNode): GroupComputedNode {
    // First, compute all children recursively
    const computedChildren = group.children.map(computeGroupDerived);

    // Compute line item totals
    const lineItemsDerived = group.lineItems.map(computeLineItemDerived);

    // Sum up budgets: own line items + children's totals (models.py:59-64)
    const lineItemsBudget = lineItemsDerived.reduce(
        (s, li) => s + li.client_budget,
        0,
    );
    const childrenBudget = computedChildren.reduce(
        (s, c) => s + c.derived.client_budget,
        0,
    );

    const lineItemsBudgetBuffered = lineItemsDerived.reduce(
        (s, li) => s + li.client_budget_buffered,
        0,
    );
    const childrenBudgetBuffered = computedChildren.reduce(
        (s, c) => s + c.derived.client_budget_buffered,
        0,
    );

    // client_quoted_pre_addon = sum(children.client_quoted) + sum(line_items.client_quoted) (models.py:71-72)
    const lineItemsQuoted = lineItemsDerived.reduce(
        (s, li) => s + li.client_quoted,
        0,
    );
    const childrenQuoted = computedChildren.reduce(
        (s, c) => s + c.derived.client_quoted,
        0,
    );
    const client_quoted_pre_addon = childrenQuoted + lineItemsQuoted;

    // Compute addon totals (percentage addons use client_quoted_pre_addon as base)
    const addonsDerived = group.addons.map((addon) =>
        computeAddonDerived(addon, client_quoted_pre_addon),
    );
    const addonsQuoted = addonsDerived.reduce((s, a) => s + a.client_quoted, 0);

    // client_quoted = deep_line_items_quoted + deep_addons_quoted (models.py:55-56)
    // deep includes children's addons too
    const deepChildrenAddonsQuoted = computedChildren.reduce(
        (s, c) =>
            s + c.derived.client_quoted - c.derived.client_quoted_pre_addon,
        0,
    );
    const client_quoted =
        client_quoted_pre_addon + addonsQuoted + deepChildrenAddonsQuoted;

    // gross profit (models.py:67-68)
    const lineItemsGrossProfit = lineItemsDerived.reduce(
        (s, li) => s + li.client_gross_profit,
        0,
    );
    const childrenGrossProfit = computedChildren.reduce(
        (s, c) => s + c.derived.client_gross_profit,
        0,
    );
    const client_gross_profit =
        lineItemsGrossProfit +
        childrenGrossProfit +
        addonsQuoted +
        deepChildrenAddonsQuoted;

    return {
        id: group.id,
        derived: {
            client_budget: lineItemsBudget + childrenBudget,
            client_budget_buffered:
                lineItemsBudgetBuffered + childrenBudgetBuffered,
            client_quoted,
            client_quoted_pre_addon,
            client_gross_profit,
        },
        lineItemsDerived,
        addonsDerived,
        children: computedChildren,
    };
}

// ── Bid-Level Calculations ─────────────────────────────────────────────────

interface BidInput {
    sections: GroupInputNode[]; // Root-level groups (parent_id === null)
    bidLevelAddons: AddonInput[]; // Addons with line_group_id === null
    allLineItems: LineItemInput[]; // Flat list of all line items for budget/quoted breakdowns
}

/**
 * Computes all derived totals for the entire bid.
 * Mirrors: Bid @cached_property chain in models.py lines 564-600
 */
export function computeBidDerived(input: BidInput): {
    bidDerived: BidDerived;
    sections: GroupComputedNode[];
} {
    const computedSections = input.sections.map(computeGroupDerived);

    // client_quoted_pre_addon = sum(sections.client_quoted) (models.py:585-586)
    const client_quoted_pre_addon = computedSections.reduce(
        (s, sec) => s + sec.derived.client_quoted,
        0,
    );

    // Bid-level addons (line_group_id === null) use bid's pre-addon total
    const bidAddonsDerived = input.bidLevelAddons.map((addon) =>
        computeAddonDerived(addon, client_quoted_pre_addon),
    );
    const addons_quoted = bidAddonsDerived.reduce(
        (s, a) => s + a.client_quoted,
        0,
    );

    // All addons across the bid (models.py:577-578)
    const deep_addons_quoted =
        addons_quoted +
        computedSections.reduce(
            (s, sec) =>
                s +
                (sec.derived.client_quoted -
                    sec.derived.client_quoted_pre_addon),
            0,
        );

    // total_quoted = client_quoted_pre_addon + bid-level addons (models.py:581-582)
    const total_quoted = client_quoted_pre_addon + addons_quoted;

    // Budget/quoted breakdowns by internal vs external (models.py:564-594)
    const allDerived = input.allLineItems.map(computeLineItemDerived);
    const external_budget = allDerived
        .filter((_, i) => !input.allLineItems[i].is_internal)
        .reduce((s, li) => s + li.client_budget_buffered, 0);
    const internal_budget = allDerived
        .filter((_, i) => input.allLineItems[i].is_internal)
        .reduce((s, li) => s + li.client_budget_buffered, 0);
    const external_quoted = allDerived
        .filter((_, i) => !input.allLineItems[i].is_internal)
        .reduce((s, li) => s + li.client_quoted, 0);
    const internal_quoted = allDerived
        .filter((_, i) => input.allLineItems[i].is_internal)
        .reduce((s, li) => s + li.client_quoted, 0);

    return {
        bidDerived: {
            client_quoted_pre_addon,
            total_quoted,
            addons_quoted,
            deep_addons_quoted,
            external_budget: roundMoney(external_budget),
            internal_budget: roundMoney(internal_budget),
            external_quoted: roundMoney(external_quoted),
            internal_quoted: roundMoney(internal_quoted),
        },
        sections: computedSections,
    };
}
